const STORAGE_KEY = 'pendingClientSubscription';

export const PENDING_CLIENT_SUBSCRIPTION_PATH = '/clients';

export type PendingClientSubscription = {
  organizationId: string;
  organizationName: string;
  createdByUserId: string;
  createdByUserIds: string[];
  createdAt: string;
};

type IdentityUser = {
  id?: string;
  keycloak_id?: string;
  userKeyCloakId?: string;
  user_id?: string;
  email?: string;
} | null | undefined;

export const resolveUserIdentityKeys = (user: IdentityUser): string[] => {
  if (!user) return [];
  return Array.from(
    new Set(
      [user.id, user.keycloak_id, user.userKeyCloakId, user.user_id, user.email]
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    ),
  );
};

export const primaryUserIdentity = (user: IdentityUser): string =>
  resolveUserIdentityKeys(user)[0] || '';

export const getPendingClientSubscription = (): PendingClientSubscription | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingClientSubscription;
    const organizationId = String(parsed?.organizationId || '').trim();
    if (!organizationId) return null;
    const createdByUserId = String(parsed?.createdByUserId || '').trim();
    const createdByUserIds = Array.isArray(parsed?.createdByUserIds)
      ? parsed.createdByUserIds.map((value) => String(value || '').trim()).filter(Boolean)
      : createdByUserId
        ? [createdByUserId]
        : [];
    return {
      organizationId,
      organizationName: String(parsed?.organizationName || '').trim(),
      createdByUserId,
      createdByUserIds,
      createdAt: String(parsed?.createdAt || '').trim(),
    };
  } catch {
    return null;
  }
};

export const savePendingClientSubscription = (pending: {
  organizationId: string;
  organizationName: string;
  user?: IdentityUser;
}): void => {
  const organizationId = pending.organizationId.trim();
  if (!organizationId) return;
  const createdByUserIds = resolveUserIdentityKeys(pending.user);
  const record: PendingClientSubscription = {
    organizationId,
    organizationName: pending.organizationName.trim(),
    createdByUserId: createdByUserIds[0] || '',
    createdByUserIds,
    createdAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
};

export const clearPendingClientSubscription = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const getPendingClientSubscriptionForUser = (
  user: IdentityUser,
): PendingClientSubscription | null => {
  const pending = getPendingClientSubscription();
  if (!pending) return null;
  const userKeys = resolveUserIdentityKeys(user);
  const pendingKeys = pending.createdByUserIds.length
    ? pending.createdByUserIds
    : pending.createdByUserId
      ? [pending.createdByUserId]
      : [];
  if (pendingKeys.length > 0 && userKeys.length > 0) {
    const pendingSet = new Set(pendingKeys);
    if (!userKeys.some((key) => pendingSet.has(key))) return null;
  }
  return pending;
};

export const getPendingClientLandingPath = (): string | null =>
  getPendingClientSubscription() ? PENDING_CLIENT_SUBSCRIPTION_PATH : null;
